import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import theme from '@shared/themes/default';

import DynamicInputList from '../DynamicInputList';

const Wrapper = styled.div`
  align-items: start;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m3};
  padding: ${theme.spacing.s};
`;

const LinksTab = ({ handleChange, socialMediaTypes, values }) => {
  const [websites, setWebsites] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [typeParser, setTypeParser] = useState({});
  const [types, setTypes] = useState([]);

  const { t } = useTranslation();

  const handleWebsitesChange = (newWebsites) => {
    const parsedWebsites = newWebsites.map((link) => {
      const { id, socialMediaTypeId, url } = link;
      return { id, socialMediaTypeId, url };
    });
    handleChange({
      name: 'socialLinks',
      value: [...socialLinks, ...parsedWebsites],
    });
    setWebsites(parsedWebsites);
  };

  const handleSocialLinksChange = (newSocialLinks) => {
    const parsedLinks = newSocialLinks.map((link) => {
      const { id, socialMediaTypeId, url } = link;
      return { id, socialMediaTypeId, url };
    });

    handleChange({
      name: 'socialLinks',
      value: [...parsedLinks, ...websites],
    });
    setSocialLinks(parsedLinks);
  };

  useEffect(() => {
    if (socialMediaTypes.length) {
      const [typeParser, types] = socialMediaTypes.reduce(
        ([parser, types], social) => {
          const newParser = parser;
          newParser[social.id] = {
            ...social,
            icon: social.icon,
            placeholder: t('backoffice.experts.linkPlaceholder', {
              website: social.name,
            }),
          };

          types.push(newParser[social.id]);

          return [newParser, types];
        },
        [{}, []]
      );

      setTypeParser(typeParser);
      setTypes(types);

      const [socialLinks, pages] = (values.socialLinks || []).reduce(
        ([socialLinks, pages], link) => {
          const type = typeParser[link.socialMediaTypeId];

          if (type.icon === 'www') {
            pages.push(link);
          } else {
            socialLinks.push(link);
          }
          return [socialLinks, pages];
        },
        [[], []]
      );

      setWebsites(pages);
      setSocialLinks(socialLinks);
    }
  }, [socialMediaTypes]);

  if (!types.length) return null;

  return (
    <Wrapper>
      <DynamicInputList
        title={t('backoffice.experts.websites')}
        emptyLabel={t('backoffice.experts.noWebsites')}
        types={[
          {
            type: 'website',
            placeholder: t('backoffice.experts.websitePlaceholder'),
          },
        ]}
        inputs={websites.map((link) => ({
          ...link,
          placeholder: typeParser[link.socialMediaTypeId].placeholder,
        }))}
        onChange={handleWebsitesChange}
      />
      <DynamicInputList
        title={t('backoffice.experts.socialMedia')}
        emptyLabel={t('backoffice.experts.noSocialMedia')}
        types={types}
        inputs={socialLinks.map((link) => ({
          ...link,
          placeholder: typeParser[link.socialMediaTypeId].placeholder,
          label: typeParser[link.socialMediaTypeId].name,
        }))}
        onChange={handleSocialLinksChange}
      />
    </Wrapper>
  );
};

LinksTab.propTypes = {
  handleChange: PropTypes.func.isRequired,
  socialMediaTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  values: PropTypes.object.isRequired,
};

export default LinksTab;
